import type { AuthMeResponse } from '@cult/schemas';
import { AuthMeResponseSchema } from '@cult/schemas';
import { useQuery } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../../../lib/api-client';
import { useCurrentUser } from '../../../state/use-current-user';
import { authKeys } from '../auth-keys';

/**
 * Fetches GET /auth/me and mirrors the user into the Zustand store. Returns null on 401.
 * Shared by the `_auth` route guard and the hook below so both see one cache entry.
 */
export const loadAuthMe = async (): Promise<AuthMeResponse | null> => {
  try {
    const raw = await apiFetch<unknown>('/auth/me');
    const me = AuthMeResponseSchema.parse(raw);
    useCurrentUser.getState().setUser(me.user);
    return me;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      useCurrentUser.getState().clearUser();
      return null;
    }
    throw err;
  }
};

export const useAuthMe = () => {
  return useQuery<AuthMeResponse | null, Error, AuthMeResponse['user'] | null>({
    queryKey: authKeys.me(),
    queryFn: loadAuthMe,
    select: (me) => me?.user ?? null,
    staleTime: 60_000,
    retry: 0,
  });
};
